import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { assessmentsAPI } from '../../utils/api';
import Button from '../shared/Button';
import RecruiterHeader from './RecruiterHeader';
import './RecruiterDashboard.css';

/**
 * RecruiterDashboard Component
 *
 * Main dashboard for recruiters to manage their assessments:
 * - View all assessments they created
 * - Filter by status (draft, scheduled, in_progress, completed, cancelled)
 * - Search by name or position
 * - Actions: Edit, Send Invitation, View Details, Cancel
 * - Create new assessment
 * - Pagination support
 */
const RecruiterDashboard = () => {
  const navigate = useNavigate();

  // State
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const ITEMS_PER_PAGE = 10;

  // Load assessments
  useEffect(() => {
    loadAssessments();
  }, [statusFilter, currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAssessments = async () => {
    setLoading(true);
    setError('');

    try {
      const params = {
        page: currentPage,
        limit: ITEMS_PER_PAGE
      };

      // Add status filter if not "all"
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await assessmentsAPI.getAll(params);

      setAssessments(response.data.assessments);
      setTotalPages(response.data.pagination.pages);
      setTotalCount(response.data.pagination.total);
    } catch (err) {
      console.error('[RecruiterDashboard] Error loading assessments:', err);
      setError(err.response?.data?.error || 'Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  // Filter assessments by search term (client-side)
  const filteredAssessments = assessments.filter(assessment => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      assessment.name.toLowerCase().includes(searchLower) ||
      assessment.position.toLowerCase().includes(searchLower) ||
      assessment.candidateId?.name?.toLowerCase().includes(searchLower) ||
      assessment.candidateId?.email?.toLowerCase().includes(searchLower)
    );
  });

  // Handle actions
  const handleEdit = (assessmentId) => {
    navigate(`/recruiter/assessments/${assessmentId}/edit`);
  };

  const handleViewDetails = (assessmentId) => {
    navigate(`/recruiter/assessments/${assessmentId}`);
  };

  const handleSendInvitation = async (assessmentId, assessmentName) => {
    if (!window.confirm(`Send invitation for "${assessmentName}"?`)) {
      return;
    }

    try {
      await assessmentsAPI.sendInvitation(assessmentId);
      setSuccessMessage('Invitation sent successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);

      // Reload assessments to update status
      loadAssessments();
    } catch (err) {
      console.error('[RecruiterDashboard] Error sending invitation:', err);
      setError(err.response?.data?.error || 'Failed to send invitation');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleCancel = async (assessmentId, assessmentName) => {
    if (!window.confirm(`Cancel assessment "${assessmentName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await assessmentsAPI.cancel(assessmentId);
      setSuccessMessage('Assessment cancelled successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);

      // Reload assessments
      loadAssessments();
    } catch (err) {
      console.error('[RecruiterDashboard] Error cancelling assessment:', err);
      setError(err.response?.data?.error || 'Failed to cancel assessment');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleCreateNew = () => {
    navigate('/recruiter/assessments/create');
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    const baseClass = 'status-badge';
    switch (status) {
      case 'draft':
        return `${baseClass} status-draft`;
      case 'scheduled':
        return `${baseClass} status-scheduled`;
      case 'active':
        return `${baseClass} status-active`;
      case 'in_progress':
        return `${baseClass} status-in-progress`;
      case 'completed':
        return `${baseClass} status-completed`;
      case 'cancelled':
        return `${baseClass} status-cancelled`;
      case 'expired':
        return `${baseClass} status-expired`;
      default:
        return baseClass;
    }
  };

  // Get status display text
  const getStatusText = (status) => {
    return status.replace('_', ' ').toUpperCase();
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Check if actions are available for status
  const canEdit = (status) => ['draft', 'scheduled'].includes(status);
  const canSendInvitation = (status, invitationSent) => status === 'draft' && !invitationSent;
  const canCancel = (status) => !['completed', 'cancelled'].includes(status);

  return (
    <div className="recruiter-dashboard">
      <RecruiterHeader currentView="assessments" />

      <div className="dashboard-header">
        <div className="header-content">
          <h1 className="dashboard-title">Assessment Management</h1>
          <p className="dashboard-subtitle">
            Manage and monitor all your candidate assessments
          </p>
        </div>
        <Button onClick={handleCreateNew} variant="primary">
          Create New Assessment
        </Button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="notification success-notification">
          <span className="notification-message">{successMessage}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="notification error-notification">
          <span className="notification-message">{error}</span>
        </div>
      )}

      {/* Filters and Search */}
      <div className="dashboard-controls">
        <div className="filter-section">
          <label htmlFor="status-filter" className="filter-label">Filter by Status:</label>
          <select
            id="status-filter"
            className="filter-select"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1); // Reset to first page
            }}
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
            <option value="active">Active</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="search-section">
          <label htmlFor="search" className="search-label">Search:</label>
          <input
            type="text"
            id="search"
            className="search-input"
            placeholder="Search by name, position, or candidate..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Summary Stats */}
      <div className="dashboard-stats">
        <div className="stat-card">
          <span className="stat-value">{totalCount}</span>
          <span className="stat-label">Total Assessments</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{filteredAssessments.length}</span>
          <span className="stat-label">Showing</span>
        </div>
      </div>

      {/* Assessments Table */}
      <div className="assessments-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading assessments...</p>
          </div>
        ) : filteredAssessments.length === 0 ? (
          <div className="empty-state">
            <h3>No Assessments Found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search or filters'
                : statusFilter !== 'all'
                ? `No assessments with status "${statusFilter}"`
                : 'Create your first assessment to get started'}
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <Button onClick={handleCreateNew} variant="primary">
                Create First Assessment
              </Button>
            )}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="assessments-table">
              <thead>
                <tr>
                  <th>Assessment Name</th>
                  <th>Position</th>
                  <th>Candidate</th>
                  <th>Mode</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssessments.map((assessment) => (
                  <tr key={assessment._id}>
                    <td className="assessment-name-cell">
                      <span className="assessment-name">{assessment.name}</span>
                      {assessment.hints?.enabled && (
                        <span className="hint-badge" title="Hints enabled">
                          HINTS
                        </span>
                      )}
                    </td>
                    <td>{assessment.position}</td>
                    <td className="candidate-cell">
                      <div className="candidate-info">
                        <span className="candidate-name">
                          {assessment.candidateId?.name || 'Unknown'}
                        </span>
                        <span className="candidate-email">
                          {assessment.candidateId?.email || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`mode-badge mode-${assessment.mode}`}>
                        {assessment.mode.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <span className={getStatusBadgeClass(assessment.status)}>
                        {getStatusText(assessment.status)}
                      </span>
                    </td>
                    <td>{formatDate(assessment.createdAt)}</td>
                    <td className="actions-cell">
                      <div className="action-buttons">
                        <button
                          className="action-btn view-btn"
                          onClick={() => handleViewDetails(assessment._id)}
                          title="View Details"
                        >
                          View
                        </button>

                        {canEdit(assessment.status) && (
                          <button
                            className="action-btn edit-btn"
                            onClick={() => handleEdit(assessment._id)}
                            title="Edit Assessment"
                          >
                            Edit
                          </button>
                        )}

                        {canSendInvitation(assessment.status, assessment.invitationSent) && (
                          <button
                            className="action-btn invite-btn"
                            onClick={() => handleSendInvitation(assessment._id, assessment.name)}
                            title="Send Invitation"
                          >
                            Invite
                          </button>
                        )}

                        {canCancel(assessment.status) && (
                          <button
                            className="action-btn cancel-btn"
                            onClick={() => handleCancel(assessment._id, assessment.name)}
                            title="Cancel Assessment"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>

          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>

          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default RecruiterDashboard;
